import {
  formatPhoneDisplay,
  formatPhoneHref,
} from '@airship-platform/common/validation';
import { type ContactInfo } from '../../../../data-access/appointments';

export const FOLLOW_UP_SUBJECT = 'Follow-Up on Your Recent Service Call';

export interface FollowUpPhone {
  value: string;
  display: string;
  memo: string | null;
}

// Smart punctuation sits outside GSM-7, so a single curly quote or em dash the
// AI tools emit drops an SMS from 153 to 67 characters per segment. Swapping to
// ASCII keeps a body carrying the ~700 character proposal link at ~7 segments
// instead of ~16.
const PLAIN_TEXT_REPLACEMENTS: Record<string, string> = {
  '‘': "'",
  '’': "'",
  '‚': "'",
  '‛': "'",
  '′': "'",
  '“': '"',
  '”': '"',
  '„': '"',
  '″': '"',
  '–': '-',
  '—': '-',
  '―': '-',
  '•': '-',
  '·': '-',
  '…': '...',
  ' ': ' ',
  ' ': ' ',
};

const PLAIN_TEXT_PATTERN = new RegExp(
  `[${Object.keys(PLAIN_TEXT_REPLACEMENTS).join('')}]`,
  'g'
);

export const toPlainText = (text: string) =>
  text.replace(
    PLAIN_TEXT_PATTERN,
    (character) => PLAIN_TEXT_REPLACEMENTS[character]
  );

export const buildFollowUpBody = (message: string, shareLink: string) =>
  [toPlainText(message).trim(), shareLink].filter(Boolean).join('\n\n');

export const buildSmsHref = (phones: string[], body: string) =>
  `sms:${phones.map(formatPhoneHref).join(',')}?&body=${encodeURIComponent(
    body
  )}`;

export const buildCallHref = (phone: string) => `tel:${formatPhoneHref(phone)}`;

export const buildMailtoHref = (
  emails: string[],
  subject: string,
  body: string
) =>
  `mailto:${emails.join(',')}?subject=${encodeURIComponent(
    subject
  )}&body=${encodeURIComponent(body)}`;

export const mobilePhonesOf = (contacts: ContactInfo[]): FollowUpPhone[] =>
  Object.values(
    contacts
      .filter((contact) => contact.type === 'MobilePhone' && !!contact.value)
      .reduce<Record<string, FollowUpPhone>>((unique, contact) => {
        const value = formatPhoneHref(contact.value);

        if (unique[value]) {
          return unique;
        }

        return {
          ...unique,
          [value]: {
            value,
            display: formatPhoneDisplay(contact.value),
            memo: contact.memo ?? null,
          },
        };
      }, {})
  );

export const emailContactsOf = (contacts: ContactInfo[]) => [
  ...new Set(
    contacts
      .filter((contact) => contact.type === 'Email' && !!contact.value)
      .map((contact) => contact.value)
      .sort()
  ),
];
